/**
 * Density Selector Component
 * UI for selecting and previewing different density scales
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Grid,
  Paper,
  Chip,
  TextField,
  IconButton,
  Alert,
  Divider,
} from '@mui/material';
import {
  Tv,
  Computer,
  Laptop,
  TabletMac,
  PhoneAndroid,
  Check,
  Refresh,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import { useThemeSettings } from '../contexts/ThemeContext';
import { densityRecommendations } from '../theme/density';

export default function DensitySelector() {
  const { density, setDensity, resetDensity, densityOptions } = useThemeSettings();
  const [preview, setPreview] = useState(density);
  
  const handleChange = (event) => {
    setPreview(event.target.value);
  };
  
  const handleApply = () => {
    setDensity(preview);
  };
  
  const handleReset = () => {
    resetDensity();
    setPreview(density);
  };
  
  const getIcon = (value) => {
    switch (value) {
      case 'compact':
        return <Laptop fontSize="small" />;
      case 'comfortable':
        return <Computer fontSize="small" />;
      case 'standard':
        return <Computer fontSize="small" />;
      case 'spacious':
        return <TabletMac fontSize="small" />;
      case 'large':
        return <Tv fontSize="small" />;
      default:
        return <Computer fontSize="small" />;
    }
  };
  
  const currentOption = densityOptions.find(opt => opt.value === preview);
  
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Display Density Settings
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        Choose a display density that works best for your screen size and viewing distance.
        Current screen: {window.innerWidth} × {window.innerHeight} ({window.devicePixelRatio}x DPI)
      </Alert>
      
      <Grid container spacing={3}>
        {/* Density Options */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Select Density
            </Typography>
            
            <RadioGroup value={preview} onChange={handleChange}>
              <Grid container spacing={2}>
                {densityOptions.map((option) => (
                  <Grid item xs={12} key={option.value}>
                    <Card 
                      sx={{ 
                        border: preview === option.value ? '2px solid' : '1px solid',
                        borderColor: preview === option.value ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.light',
                        },
                      }}
                      onClick={() => setPreview(option.value)}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <FormControlLabel
                          value={option.value}
                          control={<Radio size="small" />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                {getIcon(option.value)}
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {option.name}
                                </Typography>
                                {option.value === density && (
                                  <Chip 
                                    label="Current" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                                  Recommended for: {densityRecommendations[option.value]?.screens.join(', ')}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
            
            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<Check />}
                onClick={handleApply}
                disabled={preview === density}
                size="small"
              >
                Apply Changes
              </Button>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleReset}
                size="small"
              >
                Auto-Detect
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Live Preview */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview ({currentOption?.name})
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.default'
            }}>
              {/* Scale indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ZoomOut fontSize="small" />
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ 
                    flex: preview === 'compact' ? 0.85 : 
                          preview === 'comfortable' ? 1.0 :
                          preview === 'standard' ? 1.15 :
                          preview === 'spacious' ? 1.3 : 1.5,
                    height: 4,
                    backgroundColor: 'primary.main',
                    borderRadius: 2,
                  }} />
                  <Box sx={{ 
                    flex: preview === 'compact' ? 0.65 : 
                          preview === 'comfortable' ? 0.5 :
                          preview === 'standard' ? 0.35 :
                          preview === 'spacious' ? 0.2 : 0,
                    height: 4,
                    backgroundColor: 'divider',
                    borderRadius: 2,
                  }} />
                </Box>
                <ZoomIn fontSize="small" />
              </Box>
              
              <Typography sx={{ fontSize: currentOption?.typography.fontSize['2xl'], mb: 1 }}>
                Heading Text
              </Typography>
              <Typography sx={{ fontSize: currentOption?.typography.fontSize.base, mb: 2 }}>
                This is body text at the selected density. Notice how the text size and spacing changes.
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="small"
                  sx={{ 
                    fontSize: currentOption?.typography.fontSize.sm,
                    height: currentOption?.components.button.medium,
                  }}
                >
                  Button
                </Button>
                <Button 
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    fontSize: currentOption?.typography.fontSize.sm,
                    height: currentOption?.components.button.medium,
                  }}
                >
                  Outlined
                </Button>
                <IconButton size="small">
                  <Check sx={{ fontSize: currentOption?.components.button.small }} />
                </IconButton>
              </Box>
              
              <TextField
                size="small"
                placeholder="Input field"
                fullWidth
                sx={{ 
                  mb: 2,
                  '& .MuiInputBase-input': {
                    fontSize: currentOption?.typography.fontSize.base,
                    height: currentOption?.components.input.medium - 16,
                  }
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Chip 
                  label="Tag 1" 
                  size="small"
                  sx={{ 
                    fontSize: currentOption?.typography.fontSize.xs,
                    height: currentOption?.components.button.small,
                  }}
                />
                <Chip 
                  label="Tag 2" 
                  size="small"
                  sx={{ 
                    fontSize: currentOption?.typography.fontSize.xs,
                    height: currentOption?.components.button.small,
                  }}
                />
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Size Details
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Base font:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">
                    {currentOption?.typography.fontSize.base}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Button height:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">
                    {currentOption?.components.button.medium}px
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Grid row:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">
                    {currentOption?.components.dataGrid.rowHeight}px
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Scale factor:
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption">
                    {currentOption?.scale}x
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}